import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { ResponsiveContainer, LineChart, Line, XAxis, YAxis, Tooltip, CartesianGrid } from "recharts";
import { Brain, Layers, ClipboardCheck, Flame, ArrowRight, Loader2, BookOpen, Sparkles } from "lucide-react";
import { Button } from "@/components/ui/button";
import { ScoreRings } from "@/components/learning/ScoreRings";
import { CoachCard } from "@/components/learning/CoachCard";
import { AchievementsGrid } from "@/components/learning/AchievementsGrid";
import { LearningProfile } from "@/components/learning/LearningProfile";
import { learningApi } from "@/lib/apiClient";
import { useTranslation } from "@/context/LanguageContext";

const STATE_COLOR = { mastered: "hsl(152,52%,46%)", familiar: "hsl(206,70%,52%)", learning: "hsl(38,92%,58%)", new: "hsl(215,12%,55%)" };

const MiniStat = ({ icon: Icon, label, value, accent }) => (
    <div className="rounded-2xl border border-border bg-surface-1 p-4 transition-colors hover:bg-surface-2">
        <div className="flex items-center justify-between"><span className="mono-label text-t4">{label}</span><Icon className="h-4 w-4" style={{ color: accent }} /></div>
        <div className="mt-2 font-sans-display text-2xl font-semibold text-t1">{value}</div>
    </div>
);

export default function Learn() {
    const navigate = useNavigate();
    const { t } = useTranslation();
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        (async () => { try { setData(await learningApi.overview()); } finally { setLoading(false); } })();
    }, []);

    const ChartTip = ({ active, payload, label }) => active && payload?.length ? (
        <div className="rounded-lg border border-border bg-surface-2 px-3 py-2 text-xs"><div className="mono-label text-t4">{label}</div><div className="text-t1">{t("nav.review")}: {payload[0].value}</div></div>
    ) : null;

    if (loading) return <div className="flex min-h-[60vh] items-center justify-center"><Loader2 className="h-6 w-6 animate-spin text-[hsl(var(--primary))]" /></div>;

    const fc = data?.flashcards || {};
    const qz = data?.quizzes || {};

    return (
        <div className="mx-auto max-w-6xl px-4 py-8 sm:px-6 lg:py-10">
            <div className="flex flex-col gap-6 lg:flex-row lg:items-center lg:justify-between">
                <div>
                    <h1 className="font-sans-display text-2xl font-semibold tracking-tight text-t1 sm:text-3xl">{t("learn.title")}</h1>
                    <p className="mt-1.5 text-t3">{t("learn.subtitle")}</p>
                </div>
                <div className="rounded-2xl border border-border bg-surface-1 p-4"><ScoreRings scores={data?.scores} size={84} /></div>
            </div>

            {/* Due reviews CTA */}
            <motion.div initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} className="mt-6 flex flex-col items-start justify-between gap-3 overflow-hidden rounded-2xl border border-border bg-surface-1 p-5 sm:flex-row sm:items-center">
                <div className="flex items-center gap-3">
                    <div className="flex h-11 w-11 items-center justify-center rounded-xl bg-[hsl(var(--primary))]/15 text-[hsl(var(--primary))]"><Layers className="h-5 w-5" /></div>
                    <div>
                        <div className="font-sans-display text-base font-semibold text-t1">{t("learn.dueTitle", { n: fc.due || 0 })}</div>
                        <div className="text-sm text-t3">{t("learn.dueSubtitle")}</div>
                    </div>
                </div>
                <Button data-testid="start-review-button" className="gap-2" disabled={!fc.due} onClick={() => navigate("/review")}>{t("learn.startReview")} <ArrowRight className="h-4 w-4" /></Button>
            </motion.div>

            {/* Stats */}
            <div className="mt-6 grid grid-cols-2 gap-4 lg:grid-cols-4">
                <MiniStat icon={Layers} label={t("learn.statFlashcards")} value={fc.total || 0} accent="hsl(174,72%,46%)" />
                <MiniStat icon={Brain} label={t("learn.statMastered")} value={fc.mastered || 0} accent="hsl(152,52%,46%)" />
                <MiniStat icon={ClipboardCheck} label={t("learn.statQuizzes")} value={qz.taken || 0} accent="hsl(206,70%,52%)" />
                <MiniStat icon={Flame} label={t("learn.statAvgQuiz")} value={`${qz.avg_score || 0}%`} accent="hsl(38,92%,58%)" />
            </div>

            {/* Coach + growth */}
            <div className="mt-6 grid gap-4 lg:grid-cols-2">
                <CoachCard />
                <div className="rounded-2xl border border-border bg-surface-1 p-5">
                    <h3 className="mb-4 font-sans-display text-sm font-semibold text-t1">{t("learn.growth")}</h3>
                    <ResponsiveContainer width="100%" height={180}>
                        <LineChart data={data?.knowledge_growth || []}>
                            <CartesianGrid strokeDasharray="3 3" stroke="hsl(222 14% 18%)" vertical={false} />
                            <XAxis dataKey="label" tick={{ fill: "hsl(215 10% 58%)", fontSize: 11 }} axisLine={false} tickLine={false} />
                            <YAxis tick={{ fill: "hsl(215 10% 58%)", fontSize: 11 }} axisLine={false} tickLine={false} width={28} allowDecimals={false} />
                            <Tooltip content={<ChartTip />} />
                            <Line type="monotone" dataKey="reviews" stroke="hsl(174,72%,46%)" strokeWidth={2.5} dot={false} />
                        </LineChart>
                    </ResponsiveContainer>
                    <div className="mt-3 flex flex-wrap gap-3">
                        {Object.keys(STATE_COLOR).map((st) => (
                            <span key={st} className="inline-flex items-center gap-1.5 text-xs text-t3"><span className="h-2.5 w-2.5 rounded-full" style={{ background: STATE_COLOR[st] }} /><span>{t(`learn.states.${st}`)}</span> {fc[st] || 0}</span>
                        ))}
                    </div>
                </div>
            </div>

            {/* Concepts you're exploring — Adaptive Learning Profile */}
            <div className="mt-6">
                <LearningProfile />
            </div>

            {/* Achievements */}
            <div className="mt-8">
                <div className="mb-3 flex items-center gap-2"><Sparkles className="h-4 w-4 text-[hsl(var(--primary))]" /><h2 className="font-sans-display text-sm font-semibold text-t2">{t("learn.achievements")}</h2></div>
                <AchievementsGrid achievements={data?.achievements || []} />
            </div>

            {/* Per-document mastery */}
            <div className="mt-8">
                <div className="mb-3 flex items-center gap-2"><BookOpen className="h-4 w-4 text-[hsl(var(--primary))]" /><h2 className="font-sans-display text-sm font-semibold text-t2">{t("learn.masteryByDoc")}</h2></div>
                <div className="space-y-2">
                    {(data?.per_document || []).map((d) => (
                        <button key={d.id} data-testid="mastery-doc-row" onClick={() => navigate(`/study/${d.id}`)} className="flex w-full items-center gap-4 rounded-xl border border-border bg-surface-1 p-3 text-left transition-colors hover:bg-surface-2">
                            <div className="min-w-0 flex-1">
                                <div className="truncate text-sm font-medium text-t1">{d.title}</div>
                                <div className="mono-label text-t4">{d.cards} {t("docpicker.cards")} · {d.quizzes} {t("docpicker.quizzes")}</div>
                            </div>
                            <div className="hidden items-center gap-4 sm:flex">
                                {["reading", "understanding", "retention"].map((k) => (
                                    <div key={k} className="text-center"><div className="font-sans-display text-sm font-semibold text-t1">{d.scores[k]}</div><div className="mono-label text-[9px] text-t4">{t(`scores.${k}`).slice(0, 4)}</div></div>
                                ))}
                            </div>
                            <div className="flex items-center gap-2">
                                <div className="w-24"><div className="h-1.5 w-full overflow-hidden rounded-full bg-surface-3"><div className="h-full rounded-full bg-[hsl(var(--primary))] transition-all duration-500" style={{ width: `${d.scores.mastery}%` }} /></div></div>
                                <span className="w-8 text-right font-sans-display text-sm font-semibold text-t1">{d.scores.mastery}</span>
                            </div>
                        </button>
                    ))}
                    {(!data?.per_document || data.per_document.length === 0) && (
                        <div className="rounded-2xl border border-dashed border-border bg-surface-1 px-6 py-10 text-center text-sm text-t3">{t("learn.masteryEmpty")}</div>
                    )}
                </div>
            </div>
        </div>
    );
}
